import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  AlertTriangle,
  Clock,
  ChevronRight,
  Wrench,
  Building2,
  Package,
  ExternalLink
} from 'lucide-react';
import { pmScheduleService } from '../../services/pmScheduleService';
import { useDateFormat } from '../../hooks/useDateFormat';

const PMSchedulesDueWidget = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get overdue and upcoming (7 days) PM schedules
      const [overdueData, upcomingData] = await Promise.all([
        pmScheduleService.getOverdueSchedules(),
        pmScheduleService.getUpcomingSchedules(7)
      ]);

      // Combine and deduplicate (upcoming might include some due today)
      const overdueIds = new Set((overdueData || []).map(s => s.id));
      const uniqueUpcoming = (upcomingData || []).filter(s => !overdueIds.has(s.id));

      const combined = [
        ...(overdueData || []).map(s => ({ ...s, isOverdue: true })),
        ...uniqueUpcoming.map(s => ({ ...s, isOverdue: false }))
      ];

      setSchedules(combined);
    } catch (err) {
      console.error('Error loading PM schedule data:', err);
      setError('Failed to load PM schedules');
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="space-y-3">
            <div className="h-12 bg-gray-100 rounded"></div>
            <div className="h-12 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-4 text-red-500">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (schedules.length === 0) {
    return null; // Don't show widget if no due schedules
  }

  const overdueSchedules = schedules.filter(s => s.isOverdue);
  const upcomingSchedules = schedules.filter(s => !s.isOverdue);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-gray-900">PM Schedules Due</h3>
        </div>
        <Link
          to="/portal/cmms/pm-schedules"
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          View All
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="p-4 space-y-4">
        {/* Overdue Section */}
        {overdueSchedules.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-sm font-medium text-red-600">
                OVERDUE ({overdueSchedules.length})
              </span>
            </div>
            <div className="space-y-2">
              {overdueSchedules.slice(0, 3).map(schedule => (
                <ScheduleItem key={schedule.id} schedule={schedule} isOverdue />
              ))}
              {overdueSchedules.length > 3 && (
                <p className="text-sm text-gray-500 pl-2">
                  +{overdueSchedules.length - 3} more overdue
                </p>
              )}
            </div>
          </div>
        )}

        {/* Upcoming Section */}
        {upcomingSchedules.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium text-blue-600">
                UPCOMING ({upcomingSchedules.length})
              </span>
            </div>
            <div className="space-y-2">
              {upcomingSchedules.slice(0, 3).map(schedule => (
                <ScheduleItem key={schedule.id} schedule={schedule} />
              ))}
              {upcomingSchedules.length > 3 && (
                <p className="text-sm text-gray-500 pl-2">
                  +{upcomingSchedules.length - 3} more upcoming
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ScheduleItem = ({ schedule, isOverdue }) => {
  const { formatDate } = useDateFormat();
  const daysUntilDue = schedule.daysUntilDue || 0;
  const isToday = daysUntilDue === 0;
  const isInternal = schedule.maintenanceType === 'INTERNAL';

  let daysText;
  if (isOverdue) {
    daysText = `${Math.abs(daysUntilDue)} days overdue`;
  } else if (isToday) {
    daysText = 'Due today';
  } else {
    daysText = `${daysUntilDue} days`;
  }

  const Icon = isInternal ? Wrench : Building2;
  const typeLabel = isInternal ? 'Work Order' : 'Visit';

  return (
    <Link
      to={`/portal/cmms/pm-schedules`}
      className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 group"
    >
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          isOverdue ? 'bg-red-100' : isToday ? 'bg-yellow-100' : 'bg-blue-100'
        }`}>
          <Icon className={`w-4 h-4 ${
            isOverdue ? 'text-red-600' : isToday ? 'text-yellow-600' : 'text-blue-600'
          }`} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 text-sm">{schedule.name}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              isInternal
                ? 'bg-blue-100 text-blue-700'
                : 'bg-purple-100 text-purple-700'
            }`}>
              {typeLabel}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Package className="w-3 h-3" />
            <span>{schedule.assetName}</span>
            {schedule.supplierName && (
              <>
                <span className="text-gray-300">|</span>
                <ExternalLink className="w-3 h-3" />
                <span>{schedule.supplierName}</span>
              </>
            )}
            {schedule.assignedUserName && (
              <>
                <span className="text-gray-300">|</span>
                <span>{schedule.assignedUserName}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="text-right">
        <span className={`text-sm font-medium ${
          isOverdue ? 'text-red-600' : isToday ? 'text-yellow-600' : 'text-blue-600'
        }`}>
          {daysText}
        </span>
        {schedule.nextDueDate && (
          <div className="text-xs text-gray-400">
            {formatDate(schedule.nextDueDate)}
          </div>
        )}
      </div>
    </Link>
  );
};

export default PMSchedulesDueWidget;
